import type {
  FieldDefinition,
  FormDefinition,
  SectionDefinition,
  SectionWidth,
  SectionColor,
  VisibilityCondition,
  VisibilityGroup,
  VisibilityOperator,
  VisibilityRule,
  NotificationRule,
} from '@/types/cgt'
import { evaluateGateFormula, extractFormulaRefs, type FormulaContext } from '@/lib/formula'
import { tableAxisRefIds } from '@/lib/tableForm'

// ─── Legacy shapes (pre-86ey3c4cf) ───────────────────────────────────────────
// Forms used to persist a flat `fields` array plus a form-level `columns`
// (1 | 2) and per-field `colSpan`/`column`. `section` was a flat divider
// field-type rather than a container. These helpers read either shape so old
// definitions keep rendering until they are re-saved in the new section shape.

type LegacyField = FieldDefinition & { column?: 0 | 1 }

interface DefinitionLike {
  sections?: SectionDefinition[]
  // The backend persists sections in a JSONB column still named `fields`, so an
  // API response may carry sections here. It may also be a legacy flat field
  // list. Detected at read time via isSectionShaped().
  fields?: Array<LegacyField | SectionDefinition>
  columns?: number
}

/** A section object has a `width` and its own nested `fields` array. */
function isSectionShaped(el: unknown): el is SectionDefinition {
  return (
    !!el &&
    typeof el === 'object' &&
    Array.isArray((el as { fields?: unknown }).fields) &&
    typeof (el as { width?: unknown }).width === 'string'
  )
}

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `id-${Math.round(performance.now() * 1000)}-${Math.round(performance.now())}`
}

/** Build a section, filling defaults. */
export function makeSection(partial: Partial<SectionDefinition> = {}): SectionDefinition {
  return {
    id: partial.id ?? newId(),
    title: partial.title ?? 'Section',
    width: partial.width ?? 'full',
    order: partial.order ?? 0,
    fields: partial.fields ?? [],
  }
}

/** Drop the retired `column` prop and re-number order. `colSpan` (half/full
 *  within a section) is preserved. */
function cleanField(f: LegacyField, order: number): FieldDefinition {
  const clean = { ...f, order } as LegacyField
  delete clean.column
  return clean
}

/**
 * Convert a legacy flat field list into section containers:
 * - Each legacy `section` divider becomes a full-width container owning the
 *   fields that follow it (until the next divider).
 * - Fields before the first divider land in a default leading section.
 * - If there are no dividers but the form was 2-column, split by the old
 *   `column` index into two half-width sections.
 */
function migrateLegacyFields(fields: LegacyField[], columns?: number): SectionDefinition[] {
  const ordered = fields.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  const hasDivider = ordered.some(f => f.fieldType === 'section')

  if (!hasDivider && columns === 2) {
    const col0 = ordered.filter(f => (f.column ?? 0) !== 1)
    const col1 = ordered.filter(f => (f.column ?? 0) === 1)
    const sections: SectionDefinition[] = []
    // Synthesized sections are title-less so migrated forms keep their old
    // header-less look; authors can name them later in the builder.
    if (col0.length) {
      sections.push(makeSection({ title: '', width: 'half', order: 0, fields: col0.map(cleanField) }))
    }
    if (col1.length) {
      sections.push(makeSection({ title: '', width: 'half', order: sections.length, fields: col1.map(cleanField) }))
    }
    return sections.length ? sections : [makeSection({ title: '' })]
  }

  const sections: SectionDefinition[] = []
  let current: SectionDefinition | null = null
  const flush = () => {
    if (current) {
      current.order = sections.length
      sections.push(current)
    }
  }
  for (const f of ordered) {
    if (f.fieldType === 'section') {
      // A legacy section divider becomes a real container titled by its label.
      flush()
      current = makeSection({ title: f.label || '', width: 'full' })
    } else {
      if (!current) current = makeSection({ title: '', width: 'full' })
      current.fields.push(cleanField(f, current.fields.length))
    }
  }
  flush()
  return sections.length ? sections : [makeSection({ title: '' })]
}

/**
 * Normalize any form definition (new nested or legacy flat) to ordered
 * sections, each with its fields sorted by order. Safe to call on raw API
 * responses that predate the section model.
 */
export function getSections(def: DefinitionLike | FormDefinition): SectionDefinition[] {
  const d = def as DefinitionLike
  // Sections may arrive under `sections` or (from the backend's JSONB column)
  // under `fields`. Either way, detect the section shape and normalize.
  const candidate = d.sections && d.sections.length > 0 ? d.sections : d.fields
  if (candidate && candidate.length > 0 && isSectionShaped(candidate[0])) {
    return (candidate as SectionDefinition[])
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((s, i) => ({
        id: s.id ?? newId(),
        title: s.title ?? '',
        width: s.width ?? 'full',
        headerColor: s.headerColor,
        order: i,
        // Preserve section-level sequencing so the gate survives load/normalize.
        gatedByPrevious: s.gatedByPrevious,
        unlockedBySections: s.unlockedBySections,
        visibility: s.visibility,
        fields: (s.fields ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
      }))
  }
  // Legacy flat field list.
  return migrateLegacyFields((d.fields as LegacyField[]) ?? [], d.columns)
}

/**
 * All input fields across every section, in document order
 * (section order → field order). Use this anywhere that previously iterated
 * the flat `definition.fields` array for values/lookups.
 */
export function allFields(def: DefinitionLike | FormDefinition): FieldDefinition[] {
  return getSections(def).flatMap(s => s.fields)
}

// ─── Sequencing (field & section level) ──────────────────────────────────────

/**
 * Single source of truth for "is this field value empty?". A checkbox is empty
 * unless explicitly `true`; number 0 is non-empty.
 */
export function isValueEmpty(fieldType: string, v: unknown): boolean {
  if (fieldType === 'checkbox') return v !== true
  return v === undefined || v === '' || v === null
}

/**
 * Normalizes a field's `unlockedBy` prerequisite(s) to an id list. A field may
 * depend on multiple earlier fields (AND — it unlocks only when ALL are filled).
 * Accepts the legacy single-string shape and the multi-value array shape.
 */
export function prerequisiteIds(field: { unlockedBy?: string | string[] }): string[] {
  const u = field.unlockedBy
  if (!u) return []
  return Array.isArray(u) ? u : [u]
}

/**
 * Normalizes a section's `unlockedBySections` prerequisite(s) to an id list. A
 * section may depend on multiple earlier sections (AND — it unlocks only when ALL
 * are complete). Accepts the legacy single-string shape and the array shape.
 */
export function sectionPrerequisiteIds(section: {
  unlockedBySections?: string | string[]
}): string[] {
  const u = section.unlockedBySections
  if (!u) return []
  return Array.isArray(u) ? u : [u]
}

/**
 * Whether a section counts as "complete" for section-level sequencing.
 *
 * If the author marked any fields required, those define completion (the
 * required-only rule). Otherwise ALL fillable fields must be filled — so a gate
 * on a section that has no required fields still engages instead of being
 * trivially satisfied. Layout dividers, tables (values live under composite cell
 * keys, not the field id) and calculated fields (auto-derived) are never counted.
 */
export function isSectionComplete(
  section: SectionDefinition,
  values: Record<string, unknown>,
  ctx?: FormulaContext,
): boolean {
  const fillable = section.fields.filter(
    f =>
      f.fieldType !== 'section' &&
      f.fieldType !== 'table' &&
      f.fieldType !== 'calculated' &&
      // MR fields are auto-populated from the patient record — their value never
      // lands in submission.values, so counting them would deadlock the gate.
      !f.isMrField &&
      // A conditionally hidden field can't be filled — it must never gate,
      // or a section lock behind it deadlocks the form.
      isTargetVisible(f.visibility, values, ctx),
  )
  const required = fillable.filter(f => f.required)
  const gating = required.length > 0 ? required : fillable
  return gating.every(f => !isValueEmpty(f.fieldType, values[f.id]))
}

/**
 * Ids of sections currently locked by section-level sequencing. Each section i>0
 * gathers its prerequisite section ids from `unlockedBySections` when set, else
 * falls back to the legacy `gatedByPrevious` → the immediately-preceding section.
 * Only prerequisites that exist and are strictly EARLIER (index < i) count; a
 * section is locked when ANY such prerequisite is not yet complete. The first
 * section is never gated (nothing precedes it); locks cascade naturally because a
 * locked section's fields stay empty.
 */
export function computeLockedSectionIds(
  sections: SectionDefinition[],
  values: Record<string, unknown>,
  ctx?: FormulaContext,
): Set<string> {
  const locked = new Set<string>()
  const indexById = new Map(sections.map((s, i) => [s.id, i]))
  for (let i = 1; i < sections.length; i++) {
    const section = sections[i]
    const explicit = sectionPrerequisiteIds(section)
    const prereqIds =
      explicit.length > 0
        ? explicit
        : section.gatedByPrevious
          ? [sections[i - 1].id]
          : []
    // Keep only prerequisites that exist, sit strictly before this section,
    // and are currently visible — a hidden prerequisite section can never be
    // completed, so letting it gate would deadlock the form.
    const earlier = prereqIds.filter(id => {
      const idx = indexById.get(id)
      return (
        idx !== undefined && idx < i && isTargetVisible(sections[idx].visibility, values, ctx)
      )
    })
    if (earlier.some(id => !isSectionComplete(sections[indexById.get(id)!], values, ctx))) {
      locked.add(section.id)
    }
  }
  return locked
}

// ─── Conditional visibility (value-driven show/hide) ─────────────────────────
// Distinct from the sequencing above: sequencing enables/disables, visibility
// renders/hides. Evaluation mirrors cgt-backend src/form-engine/
// evaluate-visibility.ts — keep the two in sync.

/**
 * An empty/unanswered source value makes its condition false for every
 * operator — EXCEPT boolean comparisons: an unanswered checkbox IS unchecked,
 * so conditions against 'true'/'false' see it as false ("show when unchecked"
 * must fire on a fresh form). Other `show` targets stay hidden until the
 * driving value is set.
 *
 * A condition with `formula` set ignores sourceFieldId/operator/value
 * entirely and evaluates that Boolean formula instead (fail-closed) — the
 * escape hatch for conditions the structured form can't express, e.g. date
 * arithmetic (`TODAY() <= {expiration}`). Composes freely with structured
 * rows in the same group.
 */
function evaluateVisibilityCondition(
  cond: VisibilityCondition,
  values: Record<string, unknown>,
  ctx?: FormulaContext,
): boolean {
  if (cond.formula) return evaluateGateFormula(cond.formula, values, ctx)
  return matchesCondition(values[cond.sourceFieldId], cond.operator, cond.value)
}

/**
 * One structured comparison: the source value `raw` against the author's
 * `expected` value. Shared by conditional visibility and by the
 * out-of-specification `when` rules (lib/oos.ts), so both read a value the
 * same way. Blank handling is the visibility rule above: blank is false for
 * every operator except a 'true'/'false' comparison, where blank reads as
 * unchecked.
 */
export function matchesCondition(
  raw: unknown,
  operator: VisibilityOperator,
  expected: string | number,
): boolean {
  if (raw === undefined || raw === null || raw === '') {
    const v = String(expected)
    if (v !== 'true' && v !== 'false') return false
    raw = false
  }

  switch (operator) {
    case 'equals':
      return String(raw) === String(expected)
    case 'notEquals':
      return String(raw) !== String(expected)
    // Substring operators are case-insensitive — authors match fragments of
    // free text, where exact casing is rarely intended.
    case 'contains':
      return String(raw).toLowerCase().includes(String(expected).toLowerCase())
    case 'startsWith':
      return String(raw).toLowerCase().startsWith(String(expected).toLowerCase())
    case 'endsWith':
      return String(raw).toLowerCase().endsWith(String(expected).toLowerCase())
    default: {
      const left = toComparable(raw)
      const right = toComparable(expected)
      if (left === null || right === null) return false
      if (operator === 'gt') return left > right
      if (operator === 'gte') return left >= right
      if (operator === 'lt') return left < right
      return left <= right
    }
  }
}

/**
 * Numeric sources compare as numbers; date sources (ISO strings) fall back to
 * their epoch timestamp. Anything non-comparable disables the condition.
 */
function toComparable(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string') {
    if (v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v)
    const t = Date.parse(v)
    return Number.isNaN(t) ? null : t
  }
  return null
}

/**
 * Action-agnostic condition-tree walker — conditions inside a group combine
 * with the group's `logic`; group results combine with the outer `logic`.
 * Shared by `evaluateVisibilityExpression` (VisibilityRule's show/hide
 * action) and by any future notification-rule preview — the notification
 * rule authored in the form builder has the identical {logic, groups} shape,
 * just a different action payload. Mirrors cgt-backend
 * evaluate-visibility.ts's `evaluateConditionTree` — keep in sync.
 */
export function evaluateConditionTree(
  logic: 'and' | 'or',
  groups: VisibilityGroup[],
  values: Record<string, unknown>,
  ctx?: FormulaContext,
): boolean {
  const groupResults = groups.map(g => {
    const results = (g.conditions ?? []).map(c => evaluateVisibilityCondition(c, values, ctx))
    return g.logic === 'and' ? results.every(Boolean) : results.some(Boolean)
  })
  return logic === 'and' ? groupResults.every(Boolean) : groupResults.some(Boolean)
}

/**
 * Evaluates the whole two-level expression: conditions inside a group combine
 * with the group's `logic`; group results combine with the rule's `logic`.
 */
export function evaluateVisibilityExpression(
  rule: VisibilityRule,
  values: Record<string, unknown>,
  ctx?: FormulaContext,
): boolean {
  return evaluateConditionTree(rule.logic, rule.groups, values, ctx)
}

/**
 * Whether the target carrying `rule` is visible for the given live values.
 * A target with no rule — or a malformed one from stored JSONB — is always
 * visible; hiding on bad data would silently exempt required fields.
 */
export function isTargetVisible(
  rule: VisibilityRule | undefined,
  values: Record<string, unknown>,
  ctx?: FormulaContext,
): boolean {
  if (!rule || !Array.isArray(rule.groups)) return true
  const met = evaluateVisibilityExpression(rule, values, ctx)
  return rule.action === 'show' ? met : !met
}

/**
 * Every field referenced anywhere in a set of groups — a structured
 * condition's sourceFieldId, or every `{fieldId}` ref inside a formula
 * condition's formula string. Shared by `visibilitySourceIds` and
 * `notificationSourceIds`, since both rule shapes carry the same
 * `{logic, groups}` condition tree.
 */
function groupsSourceIds(groups: VisibilityGroup[]): string[] {
  return groups.flatMap(g =>
    g.conditions.flatMap(c => (c.formula ? extractFormulaRefs(c.formula) : [c.sourceFieldId])),
  )
}

/**
 * Every field referenced anywhere in a rule (all groups) — a structured
 * condition's sourceFieldId, or every `{fieldId}` ref inside a formula
 * condition's formula string.
 */
export function visibilitySourceIds(rule: VisibilityRule | undefined): string[] {
  if (!rule) return []
  return groupsSourceIds(rule.groups)
}

/** Same as `visibilitySourceIds`, for a field's independent notification rule. */
export function notificationSourceIds(rule: NotificationRule | undefined): string[] {
  if (!rule) return []
  return groupsSourceIds(rule.groups)
}

/**
 * Every notification rule on a field, whatever shape it is stored in.
 *
 * A field carries a LIST — one condition notifies QA, another notifies the
 * pharmacy, each with its own message, channels and audience. The original
 * shape was a single `notification` object and definitions written against it
 * are still in the database, so that is READ as a list of one rather than
 * migrated: a versioned definition is immutable, so old rows keep arriving in
 * the old shape forever.
 *
 * `notifications` wins when both are present — an author who has edited the
 * field in the current builder has a list, and the single rule is whatever was
 * there beforehand. An EMPTY list is respected, not treated as missing: it
 * means the author deleted every rule.
 *
 * Mirrors `notificationRules` in cgt-backend
 * src/form-engine/evaluate-notification-rules.ts — keep the two in sync.
 */
export function notificationRules(field: {
  notification?: NotificationRule
  notifications?: NotificationRule[]
}): NotificationRule[] {
  if (Array.isArray(field.notifications)) return field.notifications
  return field.notification ? [field.notification] : []
}

/** Ids of sections currently hidden by their own visibility rule. */
export function computeHiddenSectionIds(
  sections: SectionDefinition[],
  values: Record<string, unknown>,
  ctx?: FormulaContext,
): Set<string> {
  return new Set(
    sections.filter(s => !isTargetVisible(s.visibility, values, ctx)).map(s => s.id),
  )
}

/**
 * Ids of fields currently hidden — either by their own rule or because their
 * whole section is hidden.
 */
export function computeHiddenFieldIds(
  sections: SectionDefinition[],
  values: Record<string, unknown>,
  ctx?: FormulaContext,
): Set<string> {
  const hidden = new Set<string>()
  for (const section of sections) {
    const sectionHidden = !isTargetVisible(section.visibility, values, ctx)
    for (const f of section.fields) {
      if (sectionHidden || !isTargetVisible(f.visibility, values, ctx)) hidden.add(f.id)
    }
  }
  return hidden
}

/**
 * Whether a field must hold a value right now: statically required, or made
 * required by its visibility rule (`require: true`) while it is visible.
 * Hidden fields are never required — callers should also skip them entirely.
 */
export function isFieldRequiredNow(
  field: Pick<FieldDefinition, 'required' | 'visibility'>,
  values: Record<string, unknown>,
  ctx?: FormulaContext,
): boolean {
  if (!isTargetVisible(field.visibility, values, ctx)) return false
  return field.required || field.visibility?.require === true
}

/**
 * Drops incomplete condition rows — no source picked yet, or no comparison
 * value entered — and empty groups, so a half-edited rule never reaches the
 * API (backend 422s unknown source refs) and a valueless condition never
 * persists as an always-false rule that permanently hides its target.
 * Returns undefined when nothing usable remains.
 */
export function sanitizeVisibilityRule(
  rule: VisibilityRule | undefined,
): VisibilityRule | undefined {
  if (!rule) return undefined
  const groups = rule.groups
    .map(g => ({
      ...g,
      conditions: g.conditions.filter(c =>
        c.formula ? c.formula.trim() !== '' : c.sourceFieldId !== '' && c.value !== '',
      ),
    }))
    .filter(g => g.conditions.length > 0)
  if (groups.length === 0) return undefined
  return { ...rule, groups }
}

/**
 * Same incomplete-row/empty-group stripping as `sanitizeVisibilityRule`, plus
 * the notification-specific requirements: a non-empty message and at least
 * one selected channel. Returns undefined when nothing usable remains, so a
 * half-configured rule never reaches the API.
 */
/**
 * Sanitize a field's whole notification list, dropping the entries that would
 * never fire — an incomplete rule is an author mid-thought, not a rule.
 *
 * Returns undefined rather than `[]` when nothing survives, so a field with no
 * usable rule carries no key at all instead of an empty array that reads, on
 * the next open, as "the author deleted everything".
 */
export function sanitizeNotificationRules(
  rules: NotificationRule[] | undefined,
): NotificationRule[] | undefined {
  const kept = (rules ?? [])
    .map(r => sanitizeNotificationRule(r))
    .filter((r): r is NotificationRule => !!r)
  return kept.length > 0 ? kept : undefined
}

export function sanitizeNotificationRule(
  rule: NotificationRule | undefined,
): NotificationRule | undefined {
  if (!rule) return undefined
  const groups = rule.groups
    .map(g => ({
      ...g,
      conditions: g.conditions.filter(c =>
        c.formula ? c.formula.trim() !== '' : c.sourceFieldId !== '' && c.value !== '',
      ),
    }))
    .filter(g => g.conditions.length > 0)
  if (groups.length === 0) return undefined
  if (!rule.message?.trim()) return undefined
  if (!rule.channels || rule.channels.length === 0) return undefined
  // An audience of nobody is the same kind of incomplete as a blank message:
  // the rule would fire and reach no one, and the backend DTO rejects an empty
  // list outright, so dropping it here keeps the save from 400ing on a rule
  // the author never finished picking recipients for.
  if (rule.recipients?.mode === 'members' && !(rule.recipients.userIds?.length > 0)) return undefined
  if (rule.recipients?.mode === 'roles' && !(rule.recipients.roleIds?.length > 0)) return undefined
  return { ...rule, groups }
}

/** Minimal section shape the cycle guard needs (builder + definition shapes both fit). */
interface VisibilitySectionLike {
  id: string
  visibility?: VisibilityRule
  fields: Array<{ id: string; visibility?: VisibilityRule }>
}

/**
 * Would pointing `targetId`'s visibility (a field OR a section) at
 * `sourceFieldId` create a cycle (including self-reference)? The dependency
 * graph spans both levels: field rules, section rules, and containment edges
 * (a field is only visible while its section is), so loops that cross a
 * section boundary — section S shows-when x, x shows-when y, y inside S — are
 * caught too. Used by the builder to filter pickable source fields.
 */
export function wouldCreateVisibilityCycle(
  sections: VisibilitySectionLike[],
  targetId: string,
  sourceFieldId: string,
): boolean {
  if (sourceFieldId === targetId) return true
  const sourcesOf = new Map<string, string[]>()
  for (const section of sections) {
    const sectionSources = visibilitySourceIds(section.visibility)
    if (sectionSources.length > 0) sourcesOf.set(section.id, sectionSources)
    for (const f of section.fields) {
      const edges = visibilitySourceIds(f.visibility)
      // Containment: the field's visibility depends on its section's rule.
      if (sectionSources.length > 0) edges.push(section.id)
      if (edges.length > 0) sourcesOf.set(f.id, edges)
    }
  }
  // When the target is a section, the new rule would also add containment
  // edges from every field inside it — reaching any of those closes a loop.
  const targetSection = sections.find(s => s.id === targetId)
  const closesLoop = (id: string) =>
    id === targetId || (targetSection?.fields.some(f => f.id === id) ?? false)
  const seen = new Set<string>()
  const stack = [sourceFieldId]
  while (stack.length > 0) {
    const id = stack.pop()!
    if (closesLoop(id)) return true
    if (seen.has(id)) continue
    seen.add(id)
    stack.push(...(sourcesOf.get(id) ?? []))
  }
  return false
}

/** What kind of surface a field dependent lives on — see `findFieldDependents`. */
export type DependentSurface =
  | 'calculated'
  | 'unlock_gate'
  | 'signature_gate'
  | 'visibility'
  | 'table_cell'

export const DEPENDENT_SURFACE_LABELS: Record<DependentSurface, string> = {
  calculated: 'calculated field',
  unlock_gate: 'unlock gate',
  signature_gate: 'signature gate',
  visibility: 'visibility condition',
  table_cell: 'table-cell formula',
}

export interface FieldDependent {
  id: string
  label: string
  surface: DependentSurface
}

/** Minimal field/section shape the dependent lookup needs — builder
 *  (FormField/FormSection) and definition (FieldDefinition/SectionDefinition)
 *  shapes both fit without a cast. */
interface DependentFieldLike {
  id: string
  label: string
  type?: string // builder shape
  fieldType?: string // definition shape
  formula?: string
  unlockGate?: { formula: string }
  signatureGate?: { formula: string }
  visibility?: VisibilityRule
  tableConfig?: {
    columns?: { id: string }[]
    rows?: { id: string }[]
    cells?: Record<string, { formula?: string; disabled?: boolean }>
  }
}

interface DependentSectionLike {
  id: string
  title: string
  visibility?: VisibilityRule
  fields: DependentFieldLike[]
}

/**
 * Every calculated field, unlock gate, signature gate, visibility condition
 * (field- or section-level) and table-cell formula anywhere in `sections`
 * that references `targetId` — the reverse index from field id to
 * referencing expression that this codebase didn't have before z8nrz7d0h6.
 * One implementation, shared by the type-change confirmation and the
 * delete-field confirmation.
 *
 * Deliberately excludes `unlockedBy` (the plain prerequisite-id list): that
 * mechanism already self-heals on delete (see `deleteField` in
 * FormEditorPage, which prunes the deleted id from every `unlockedBy`) and
 * never degrades into the `⚠ deleted field` token this ticket is about.
 *
 * Cross-form references (another form's formula pointing at this field via
 * `{handle.fieldId}`) are out of reach here — no ticket describes a reverse
 * lookup across forms — so callers must say so rather than imply this list
 * is exhaustive.
 */
export function findFieldDependents(
  sections: DependentSectionLike[],
  targetId: string,
): FieldDependent[] {
  // A table is read through its cells and totals as well as by its own id, so
  // deleting it breaks those refs too. Its own cell formulas go with it, so
  // they are not reported.
  const target = sections.flatMap(s => s.fields).find(f => f.id === targetId)
  const ids = new Set([targetId, ...tableRefIds(target)])
  return dependentsReading(sections, ids, targetId)
}

/**
 * Every formula on the form (calculated field, gate, visibility condition,
 * table-cell formula) that reads any of `refIds`: cell keys `{rowId__colId}`
 * and/or table total tokens. Used to warn when a cell is marked not applicable
 * (a total skips such a cell by itself, but a direct reference would read a
 * cell that never has a value), and before a table row or column is removed.
 * Cell formulas on cells that are themselves not applicable are skipped.
 */
export function findCellDependents(
  sections: DependentSectionLike[],
  refIds: string[],
): FieldDependent[] {
  return dependentsReading(sections, new Set(refIds))
}

/** Every cell key and total token of a table field, or none for any other field. */
function tableRefIds(field: DependentFieldLike | undefined): string[] {
  const config = field?.tableConfig
  if (!field || (field.type ?? field.fieldType) !== 'table' || !config) return []
  const grid = { columns: config.columns ?? [], rows: config.rows ?? [] }
  return [
    ...grid.columns.flatMap(c => tableAxisRefIds(field.id, grid, 'col', c.id)),
    ...grid.rows.flatMap(r => tableAxisRefIds(field.id, grid, 'row', r.id)),
  ]
}

function dependentsReading(
  sections: DependentSectionLike[],
  ids: ReadonlySet<string>,
  skipTableId?: string,
): FieldDependent[] {
  const reads = (refs: string[]) => refs.some(r => ids.has(r))
  const deps: FieldDependent[] = []
  for (const section of sections) {
    if (reads(visibilitySourceIds(section.visibility))) {
      deps.push({ id: section.id, label: section.title || 'Untitled section', surface: 'visibility' })
    }
    for (const field of section.fields) {
      const fieldType = field.type ?? field.fieldType
      const label = field.label || 'Untitled'
      if (fieldType === 'calculated' && field.formula && reads(extractFormulaRefs(field.formula))) {
        deps.push({ id: field.id, label, surface: 'calculated' })
      }
      if (field.unlockGate?.formula && reads(extractFormulaRefs(field.unlockGate.formula))) {
        deps.push({ id: field.id, label, surface: 'unlock_gate' })
      }
      if (field.signatureGate?.formula && reads(extractFormulaRefs(field.signatureGate.formula))) {
        deps.push({ id: field.id, label, surface: 'signature_gate' })
      }
      if (reads(visibilitySourceIds(field.visibility))) {
        deps.push({ id: field.id, label, surface: 'visibility' })
      }
      if (fieldType === 'table' && field.id !== skipTableId && field.tableConfig?.cells) {
        const hasCellRef = Object.values(field.tableConfig.cells).some(
          cell => !cell.disabled && cell.formula && reads(extractFormulaRefs(cell.formula)),
        )
        if (hasCellRef) deps.push({ id: field.id, label, surface: 'table_cell' })
      }
    }
  }
  return deps
}

/** Valid section widths, ordered widest → narrowest (for pickers). */
export const SECTION_WIDTHS: SectionWidth[] = ['full', 'half', 'third', 'quarter']

/** Tailwind/grid span (out of 12) for a section width. */
export const SECTION_WIDTH_SPAN: Record<SectionWidth, number> = {
  full: 12,
  half: 6,
  third: 4,
  quarter: 3,
}

export const SECTION_WIDTH_LABELS: Record<SectionWidth, string> = {
  full: 'Full',
  half: 'Half',
  third: 'Third',
  quarter: 'Quarter',
}

/** All predefined section header colors, in palette order. */
export const SECTION_COLORS: SectionColor[] = ['none', 'slate', 'blue', 'green', 'purple', 'amber', 'rose']

/** Human-readable label per color token. */
export const SECTION_COLOR_LABELS: Record<SectionColor, string> = {
  none: 'Default',
  slate: 'Slate',
  blue: 'Blue',
  green: 'Green',
  purple: 'Purple',
  amber: 'Amber',
  rose: 'Rose',
}

/**
 * Tailwind classes for the section header strip background per color token.
 * Literal strings so Tailwind's JIT scanner keeps them all in the bundle.
 * `none` keeps the existing `bg-muted/40` base class (applied separately) — no extra class.
 */
export const SECTION_COLOR_HEADER_CLASS: Record<SectionColor, string> = {
  none: '',
  slate: 'bg-slate-100',
  blue: 'bg-blue-50',
  green: 'bg-green-50',
  purple: 'bg-purple-50',
  amber: 'bg-amber-50',
  rose: 'bg-rose-50',
}

/**
 * Tailwind classes for the color swatch dot shown in the Properties panel picker.
 * Literal strings for JIT safety.
 */
export const SECTION_COLOR_SWATCH_CLASS: Record<SectionColor, string> = {
  none: 'bg-muted border-2 border-dashed border-muted-foreground/30',
  slate: 'bg-slate-300',
  blue: 'bg-blue-400',
  green: 'bg-green-400',
  purple: 'bg-purple-400',
  amber: 'bg-amber-400',
  rose: 'bg-rose-400',
}
